<template>
  <textarea
    :id="id"
    :aria-describedby="ariaDescribedby || undefined"
    :aria-invalid="ariaInvalid || undefined"
    :aria-label="ariaLabel || undefined"
    class="base-textarea"
    :disabled="disabled"
    :placeholder="placeholder"
    :readonly="readonly"
    :required="required"
    :rows="rows"
    :value="modelValue"
    @input="handleInput"
  />
</template>

<script setup lang="ts">
/**
 * Multi-line textarea component with v-model support
 *
 * Provides a styled textarea for longer text entry with vertical
 * resizing, automatic height based on rows, and accessibility features.
 *
 * @example Basic textarea
 * ```vue
 * <BaseTextarea v-model="comments" placeholder="Enter your comments" />
 * ```
 *
 * @example With custom rows
 * ```vue
 * <BaseTextarea
 *   v-model="description"
 *   :rows="10"
 *   placeholder="Product description"
 *   required
 * />
 * ```
 *
 * @example With accessibility labels
 * ```vue
 * <BaseTextarea
 *   v-model="feedback"
 *   aria-label="Customer feedback"
 *   aria-describedby="feedback-hint"
 * />
 * ```
 */
export interface BaseTextareaProps {
  /** Current value (use with v-model) */
  modelValue: string;

  /** Placeholder text shown when textarea is empty */
  placeholder?: string;

  /**
   * Number of visible text rows (affects initial height)
   * Textarea remains vertically resizable by user
   */
  rows?: number;

  /**
   * Disables the textarea and prevents interaction
   * @default false
   */
  disabled?: boolean;

  /**
   * Makes textarea read-only (value visible but not editable)
   * @default false
   */
  readonly?: boolean;

  /**
   * Marks field as required for form validation
   * @default false
   */
  required?: boolean;

  /** Unique element ID for label association */
  id?: string;

  /** Accessible label for screen readers (use when visual label not present) */
  ariaLabel?: string;

  /** ID of element describing this textarea (typically error/hint message ID) */
  ariaDescribedby?: string;

  /** Indicates the textarea has an error for accessibility */
  ariaInvalid?: boolean;
}

withDefaults(defineProps<BaseTextareaProps>(), {
  disabled: false,
  readonly: false,
  required: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const handleInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  emit('update:modelValue', target.value);
};
</script>

<style>
.base-textarea {
  width: var(--input-width);
  padding: var(--input-padding);
  line-height: var(--input-line-height);
  border-width: var(--input-border-width-top) var(--input-border-width-right) var(--input-border-width-bottom)
    var(--input-border-width-left);
  border-style: solid;
  border-color: var(--input-border-color);
  border-radius: var(--input-border-radius-left) var(--input-border-radius-right) var(--input-border-radius-right)
    var(--input-border-radius-left);
  font-family: var(--input-font-family);
  font-size: var(--input-font-size);
  background-color: var(--input-background);
  transition: var(--input-transition);
  min-height: 6em;
  resize: vertical;
  &:focus-visible {
    outline: var(--focus-ring-width) solid var(--focus-ring-color);
    outline-offset: var(--focus-ring-offset);
  }

  &:disabled {
    background-color: var(--input-disabled-background);
    cursor: not-allowed;
    opacity: var(--input-disabled-opacity);
  }

  &::placeholder {
    color: var(--color-neutral-500);
  }
}
</style>
